/**
 * Agent Service
 * SSE-based streaming agent service for communicating with Paean AI cloud
 */

import { getToken, getApiUrl } from '../utils/config.js';
import type { CliModeOptions } from './cli-mode.js';
import type {
    AgentStreamEvent,
    AgentStreamCallbacks,
    McpState,
    McpToolResult,
} from './types.js';

/**
 * Extract a human-readable error message from an unknown value.
 * Handles: string, Error, { error: string | { message } }, { message }, plain objects.
 */
export function extractErrorMessage(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message;
    if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        // { error: "string" } or { error: { message: "string" } }
        if (obj.error) {
            if (typeof obj.error === 'string') return obj.error;
            if (typeof obj.error === 'object' && obj.error !== null) {
                const inner = obj.error as Record<string, unknown>;
                if (typeof inner.message === 'string') return inner.message;
            }
        }
        if (typeof obj.message === 'string') return obj.message;
    }
    try {
        const s = String(value);
        return s === '[object Object]' ? 'Unknown error' : s;
    } catch {
        return 'Unknown error';
    }
}

/** CLI model tier: Paean Lite / Flash / Pro */
export type CliModelTier = 'lite' | 'flash' | 'pro';

/**
 * Stream options for agent messages
 */
export interface StreamOptions {
    conversationId?: string;
    mcpState?: McpState;
    cliMode?: Partial<CliModeOptions>;
    references?: Array<{ type: string; id: string; content?: string }>;
    modelTier?: CliModelTier;
}

/**
 * Agent Service class for streaming communication
 */
export class AgentService {
    private abortController: AbortController | null = null;

    /**
     * Stream a message to the agent and handle SSE events
     */
    async streamMessage(
        message: string,
        callbacks: AgentStreamCallbacks,
        options?: StreamOptions
    ): Promise<{ abort: () => void }> {
        this.abortController = new AbortController();
        const { conversationId, mcpState, references } = options || {};

        const token = getToken();
        if (!token) {
            callbacks.onError?.('Authentication required. Run `paean login` first.');
            return { abort: () => this.abortController?.abort() };
        }

        try {
            const response = await fetch(`${getApiUrl()}/agent/cli/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    Accept: 'text/event-stream',
                },
                body: JSON.stringify({
                    message,
                    conversationId,
                    mcpState,
                    cliMode: options?.cliMode,
                    modelTier: options?.modelTier || 'flash',
                    references,
                }),
                signal: this.abortController.signal,
            });

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = extractErrorMessage(errorData) || response.statusText || errorMessage;
                } catch {
                    errorMessage = response.statusText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            if (!response.body) {
                throw new Error('No response body');
            }

            // Process the SSE stream
            await this.processStream(response.body, callbacks, conversationId);
        } catch (error) {
            if ((error as Error).name === 'AbortError') {
                // User aborted, this is expected
                return { abort: () => { } };
            }
            const errorMessage = error instanceof Error 
                ? error.message 
                : 'Unknown error occurred';
            callbacks.onError?.(errorMessage);
        }

        return { abort: () => this.abortController?.abort() };
    }

    /**
     * Process SSE stream
     */
    private async processStream(
        body: ReadableStream<Uint8Array>,
        callbacks: AgentStreamCallbacks,
        conversationId?: string
    ): Promise<void> {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let receivedDoneEvent = false;
        const signal = this.abortController?.signal;

        type ReadResult = { done: boolean; value?: Uint8Array };

        // Race each read against the abort signal so we don't block on a
        // stalled connection after the user presses Ctrl+C / Escape.
        const readWithAbort = async (): Promise<ReadResult> => {
            if (signal?.aborted) return { done: true, value: undefined };

            return new Promise<ReadResult>((resolve, reject) => {
                const onAbort = () => {
                    reader.cancel().catch(() => {});
                    resolve({ done: true, value: undefined });
                };
                if (signal?.aborted) { onAbort(); return; }
                signal?.addEventListener('abort', onAbort, { once: true });

                reader.read().then(
                    (result) => { signal?.removeEventListener('abort', onAbort); resolve(result); },
                    (err) => { signal?.removeEventListener('abort', onAbort); reject(err); },
                );
            });
        };

        try {
            while (true) {
                if (signal?.aborted) {
                    await reader.cancel().catch(() => {});
                    break;
                }

                const { done, value } = await readWithAbort();

                if (done) break;
                if (signal?.aborted) {
                    await reader.cancel().catch(() => {});
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    // Bail out of event processing immediately on abort
                    if (signal?.aborted) break;

                    if (line.startsWith('data: ')) {
                        try {
                            const eventData = JSON.parse(line.slice(6)) as AgentStreamEvent;
                            if (eventData.type === 'done') {
                                receivedDoneEvent = true;
                            }
                            await this.handleEvent(eventData, callbacks, conversationId);
                        } catch {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }

        if (!receivedDoneEvent && !signal?.aborted) {
            callbacks.onError?.('Stream ended unexpectedly. The server may have disconnected.');
        }
    }

    /**
     * Handle individual SSE events
     */
    private async handleEvent(
        event: AgentStreamEvent,
        callbacks: AgentStreamCallbacks,
        conversationId?: string
    ): Promise<void> {
        switch (event.type) {
            case 'content':
                if (event.data.thinking) {
                    callbacks.onThinking?.(true);
                } else if (event.data.text !== undefined) {
                    callbacks.onContent?.(event.data.text, event.data.partial ?? false);
                }
                break;

            case 'tool_call':
                if (event.data.id && event.data.name) {
                    callbacks.onToolCall?.(event.data.id, event.data.name);
                }
                break;

            case 'tool_result':
                if (event.data.id && event.data.name) {
                    callbacks.onToolResult?.(
                        event.data.id,
                        event.data.name,
                        event.data.result
                    );
                    
                    // Check if this is a sparklet creation or update result
                    if (
                        (event.data.name === 'generateSparklet' ||
                            event.data.name === 'updateSparklet') &&
                        event.data.result
                    ) {
                        const result = event.data.result as {
                            success?: boolean;
                            sparkletHashKey?: string;
                            title?: string;
                            message?: string;
                        };
                        if (result.success && result.sparkletHashKey) {
                            const title =
                                result.title ||
                                result.message?.match(/sparklet ["'](.+?)["']/i)?.[1] ||
                                'Sparklet';
                            callbacks.onSparkletCreated?.(result.sparkletHashKey, title);
                        }
                    }
                    
                    // Check if this is a media generation result (image or video)
                    if (
                        (event.data.name === 'generateImage' ||
                            event.data.name === 'generateVideo') &&
                        event.data.result
                    ) {
                        const result = event.data.result as {
                            success?: boolean;
                            assetHashKey?: string;
                            assetType?: 'image' | 'video';
                            fileName?: string;
                            signedUrl?: string;
                            generationPrompt?: string;
                            aiModel?: string;
                        };
                        if (result.success && result.assetHashKey) {
                            callbacks.onMediaGenerated?.({
                                assetHashKey: result.assetHashKey,
                                assetType:
                                    result.assetType ||
                                    (event.data.name === 'generateImage' ? 'image' : 'video'),
                                fileName: result.fileName,
                                signedUrl: result.signedUrl,
                                generationPrompt: result.generationPrompt,
                                aiModel: result.aiModel,
                            });
                        }
                    }
                }
                break;

            case 'sparklet_ready':
                // Handle sparklet_ready event - when sparklet HTML is generated and ready
                if (event.data.sparkletHashKey) {
                    callbacks.onSparkletCreated?.(
                        event.data.sparkletHashKey,
                        event.data.title || 'Sparklet'
                    );
                }
                break;

            case 'mcp_tool_call':
                if (
                    event.data.callId &&
                    event.data.serverName &&
                    event.data.toolName &&
                    callbacks.onMcpToolCall
                ) {
                    const mcpConversationId =
                        event.data.conversationId || conversationId;

                    let args: Record<string, unknown> = {};
                    if (event.data.argumentsJson) {
                        try {
                            args = JSON.parse(event.data.argumentsJson);
                        } catch {
                            args = {};
                        }
                    } else if (event.data.arguments) {
                        args = event.data.arguments;
                    }

                    // Race MCP tool execution against the abort signal so a
                    // hanging tool call doesn't freeze the entire CLI.
                    const MCP_TOOL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
                    const abortSignal = this.abortController?.signal;

                    try {
                        const toolPromise = callbacks.onMcpToolCall(
                            event.data.callId,
                            event.data.serverName,
                            event.data.toolName,
                            args
                        );

                        const result = await Promise.race([
                            toolPromise,
                            new Promise<never>((_, reject) => {
                                if (abortSignal?.aborted) {
                                    reject(new DOMException('Aborted', 'AbortError'));
                                    return;
                                }
                                const onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
                                abortSignal?.addEventListener('abort', onAbort, { once: true });
                                // Also timeout after MCP_TOOL_TIMEOUT_MS
                                setTimeout(() => {
                                    abortSignal?.removeEventListener('abort', onAbort);
                                    reject(new Error(`MCP tool '${event.data.toolName}' timed out after ${MCP_TOOL_TIMEOUT_MS / 1000}s`));
                                }, MCP_TOOL_TIMEOUT_MS);
                            }),
                        ]);

                        if (mcpConversationId) {
                            await this.submitMcpResult(
                                mcpConversationId,
                                event.data.callId,
                                result
                            );
                        }
                    } catch (error) {
                        if ((error as Error).name === 'AbortError') break;

                        const errorMessage = error instanceof Error 
                            ? error.message 
                            : 'Unknown error occurred';
                        if (mcpConversationId) {
                            await this.submitMcpResult(
                                mcpConversationId,
                                event.data.callId,
                                {
                                    content: [
                                        { type: 'text', text: `Error: ${errorMessage}` },
                                    ],
                                    isError: true,
                                }
                            );
                        }
                    }
                }
                break;

            case 'grounding':
                if (event.data.sources && Array.isArray(event.data.sources)) {
                    callbacks.onGrounding?.(event.data.sources);
                }
                break;

            case 'done':
                callbacks.onDone?.(event.data.conversationId);
                break;

            case 'error':
                callbacks.onError?.(
                    typeof event.data.error === 'string'
                        ? event.data.error
                        : extractErrorMessage(event.data.error) || 'Unknown error'
                );
                break;
        }
    }

    /**
     * Submit MCP tool result back to the API
     */
    async submitMcpResult(
        conversationId: string,
        callId: string,
        result: McpToolResult
    ): Promise<boolean> {
        const token = getToken();
        if (!token) return false;

        try {
            const response = await fetch(`${getApiUrl()}/agent/mcp-result`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ conversationId, callId, result }),
            });

            const data = (await response.json()) as { success?: boolean };
            return data.success === true;
        } catch {
            return false;
        }
    }

    /**
     * Abort the current stream
     */
    abort(): void {
        this.abortController?.abort();
    }
}

// Export singleton instance
export const agentService = new AgentService();
